import React from "react";
import { useSelector } from "react-redux";
import { createTheme, useMediaQuery } from "@mui/material";

import { selectIsValidEntity } from "store/authSlice";
import { useLocalStorage } from "hooks/useLocalStorage";

const SidebarLayoutContext = React.createContext(null);

export const useSidebarLayoutContext = () =>
  React.useContext(SidebarLayoutContext);

const drawerWidth = 250;

const SidebarLayoutContextProvider = ({ children }) => {
  const isMobile = useMediaQuery("(max-width: 899px)");

  const [showSidebar, setShowSidebar] = React.useState(!isMobile);
  const [party, setParty] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(false);

  const [shipmentDetailViewMode, setShipmentDetailViewMode] = useLocalStorage(
    "SHIPMENT_DETAIL_VIEW_MODE",
    "default",
  );

  const [preferredMode, setPreferredMode] = useLocalStorage(
    "PREFERRED_MODE",
    "light",
  );

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
    },
  });

  const validEntity = useSelector(selectIsValidEntity);

  // methods
  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
    setPreferredMode(darkMode ? "light" : "dark");
  };

  React.useEffect(() => {
    if (validEntity && !isMobile) setShowSidebar(true);
  }, [setShowSidebar, validEntity, isMobile]);

  React.useEffect(() => {
    setShowSidebar(!isMobile);
  }, [isMobile]);

  React.useEffect(() => {
    //tailwind darkMode configuration
    if (
      preferredMode === "dark" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    setDarkMode(preferredMode === "dark");

    return () => {
      setDarkMode(false);
    };
  }, [preferredMode]);

  return (
    <SidebarLayoutContext.Provider
      value={{
        showSidebar,
        setShowSidebar,
        isMobile,
        party,
        setParty,
        drawerWidth,
        validEntity,
        theme,
        darkMode,
        toggleDarkMode,
        shipmentDetailViewMode,
        setShipmentDetailViewMode,
      }}
    >
      {children}
    </SidebarLayoutContext.Provider>
  );
};

export default SidebarLayoutContextProvider;
